// Pure state-transition helpers used by FlowEngine.advance() and traverse().
// Each helper takes state in and returns new state — no mutation, no `this`.

import { removeDeprecatedOptions, fieldsChanged } from './utils';

// Snapshot the current item's fields/values onto itemsStates so that later
// fieldsChanged detection has a baseline. Mirrors the pre-walk seeding that
// used to live inline at the top of advance().
export function seedCurrentItemState(itemsStates, currentItem) {
    const currentItemId = currentItem.id;
    let next = { ...itemsStates };
    if (!next[currentItemId]) next[currentItemId] = {};
    next[currentItemId] = {
        ...next[currentItemId],
        fields: currentItem.fields,
    };
    if (!('values' in next[currentItemId])) {
        next[currentItemId] = {
            ...next[currentItemId],
            values: Array((currentItem.fields || []).length),
        };
    }
    return next;
}

// Call chooseNext on `fromItem`, resolve a string result to the item object,
// and call init on the result. Throws on chooseNext error so callers set the
// error policy (advance → errorRaised event, traverse → swallow-and-break).
export function resolveNextItem(items, fromItem, itemsStates, globals, loops) {
    const result = fromItem.chooseNext(itemsStates, globals, loops);
    const nextItem = typeof result === 'string' ? items[result] : result;
    nextItem.init(itemsStates, globals, loops);
    return nextItem;
}

// Walk the item's fields and drop any stored dropdown values whose option has
// since been removed from the field definition. Returns a new itemsStates; if
// nothing changed, returns the input reference.
export function applyDeprecatedOptionsCleanup(itemsStates, item, itemId) {
    const nextItemState = itemsStates[itemId];
    if (!nextItemState) return itemsStates;
    let updatedState = nextItemState;
    (item.fields || []).forEach((field, idx) => {
        updatedState = removeDeprecatedOptions(updatedState, field, idx);
    });
    if (updatedState === nextItemState) return itemsStates;
    return { ...itemsStates, [itemId]: updatedState };
}

// Write the previous item's answer into the previous loop iteration. If we
// are exiting the previous loop into an ancestor scope (next loopName is a
// prefix of prev loopName), mark the iteration as valid. Returns a new loops
// object; if there is no previous loop or the loop is unknown, returns input
// reference.
export function persistLoopIterationAnswer(
    loops, prevLoopName, prevLoopIndex, prevItemId, itemsStates, nextItemLoopName,
) {
    if (!prevLoopName || !loops[prevLoopName]) return loops;
    const prevLoop = loops[prevLoopName];
    const prevIdx = prevLoopIndex - 1;
    const prevStates = [...prevLoop.states];
    prevStates[prevIdx] = {
        ...prevStates[prevIdx],
        [prevItemId]: itemsStates[prevItemId],
    };
    if (nextItemLoopName && prevLoopName.startsWith(nextItemLoopName)) {
        const vi = [...prevLoop.validIterations];
        vi[vi.length - 1] = true;
        return { ...loops, [prevLoopName]: { ...prevLoop, states: prevStates, validIterations: vi } };
    }
    return { ...loops, [prevLoopName]: { ...prevLoop, states: prevStates } };
}

// Reconcile next item's state with its current field definitions:
// - If stored state's fields differ from rebuilt fields, drop it.
// - Else, if it's a non-decision item with no stored state and has
//   defaultValues, seed those defaults and validate.
// Returns { itemsStates, hasErrors }; hasErrors is `undefined` when no
// validation was run, signaling to the caller that the previous value
// should be preserved.
export function syncNextItemState(itemsStates, globals, loops, nextItem) {
    if (fieldsChanged(itemsStates[nextItem.id], nextItem.fields)) {
        const next = { ...itemsStates };
        delete next[nextItem.id];
        return { itemsStates: next, hasErrors: undefined };
    }
    if (
        !nextItem.isDecisionNode
        && nextItem.defaultValues
        && itemsStates[nextItem.id] === undefined
    ) {
        const next = {
            ...itemsStates,
            [nextItem.id]: { values: nextItem.defaultValues },
        };
        nextItem.validate(next, globals, loops);
        return { itemsStates: next, hasErrors: (nextItem.errors || []).length > 0 };
    }
    return { itemsStates, hasErrors: undefined };
}

// Handle arrival at an item inside a loop. Covers:
// - new loop creation on first entry
// - child-loop index reset when arriving at an ancestor loop
// - iteration reset on re-entry from outside
// - seeding a fresh state slot on a new iteration
// - dropping stored answer if fields changed since recording
// - seeding defaults (with validation) on empty slot
// - pulling stored answer into itemsStates for non-firstItemId items
// Returns { loops, itemsStates, hasErrors }.
export function reconcileLoopEntry(loops, itemsStates, globals, nextItem, previousLoopName) {
    const loopName = nextItem.loopName;
    let hasErrors = false;
    let loop = loops[loopName];
    if (loop === undefined) {
        const firstState = { [nextItem.id]: {} };
        loop = {
            index: 1,
            states: [firstState],
            firstItemId: nextItem.id,
            validIterations: [false],
        };
        loops = { ...loops, [loopName]: loop };
        return { loops, itemsStates, hasErrors };
    }
    loops = { ...loops };
    for (const name of Object.keys(loops)) {
        if (name !== loopName && name.startsWith(loopName)) {
            loops[name] = { ...loops[name], index: 1 };
        }
    }
    if (nextItem.id === loop.firstItemId) {
        let updatedLoop = { ...loop };
        if (previousLoopName !== loopName) {
            updatedLoop.index = 1;
        }
        const iterState = updatedLoop.states[updatedLoop.index - 1];
        if (iterState !== undefined) {
            if (iterState[nextItem.id] !== undefined) {
                itemsStates = {
                    ...itemsStates,
                    [nextItem.id]: iterState[nextItem.id],
                };
                if (updatedLoop.validIterations === undefined) {
                    updatedLoop.validIterations = [false];
                }
            } else {
                updatedLoop.states = [...updatedLoop.states];
                updatedLoop.states[updatedLoop.index - 1] = {
                    ...iterState,
                    [nextItem.id]: {},
                };
            }
        } else {
            updatedLoop.states = [...updatedLoop.states];
            updatedLoop.states[updatedLoop.index - 1] = { [nextItem.id]: {} };
            updatedLoop.validIterations = [...(updatedLoop.validIterations || [])];
            updatedLoop.validIterations[updatedLoop.index - 1] = false;
        }
        loops[loopName] = updatedLoop;
    }
    const currentLoop = loops[loopName];
    const currentIterIdx = currentLoop.index - 1;
    const currentIter = currentLoop.states[currentIterIdx];
    if (currentIter !== undefined) {
        const stored = currentIter[nextItem.id];
        if (stored && stored.fields && fieldsChanged(stored, nextItem.fields)) {
            const newIter = { ...currentIter };
            delete newIter[nextItem.id];
            const newStates = [...currentLoop.states];
            newStates[currentIterIdx] = newIter;
            loops[loopName] = { ...currentLoop, states: newStates };
        }
        const refreshedLoop = loops[loopName];
        const refreshedIter = refreshedLoop.states[currentIterIdx];
        if (refreshedIter[nextItem.id] === undefined) {
            itemsStates = { ...itemsStates, [nextItem.id]: {} };
            if (nextItem.defaultValues && !nextItem.isDecisionNode) {
                const seeded = { values: nextItem.defaultValues };
                const newIter2 = { ...refreshedIter, [nextItem.id]: seeded };
                const newStates2 = [...refreshedLoop.states];
                newStates2[currentIterIdx] = newIter2;
                loops[loopName] = { ...refreshedLoop, states: newStates2 };
                itemsStates = { ...itemsStates, [nextItem.id]: seeded };
                nextItem.validate(itemsStates, globals, loops);
                hasErrors = (nextItem.errors || []).length > 0;
            }
        } else if (nextItem.id !== currentLoop.firstItemId) {
            itemsStates = { ...itemsStates, [nextItem.id]: refreshedIter[nextItem.id] };
        }
    }
    return { loops, itemsStates, hasErrors };
}
